import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { SerialCapture, cleanSerials } from "@/components/logistics/SerialCapture";
import { useLookups } from "@/hooks/useLookups";
import { useFetch } from "@/hooks/useFetch";
import { api, errMsg } from "@/lib/api";
import { fmtDate } from "@/lib/format";

const sel = "h-8 w-full rounded border border-slate-200 bg-white px-2 text-sm";

// LM has no Asset Group yet: pick one of the customer's groups and link it before creating assets
function LinkGroup({ order, onLinked }) {
  const { data } = useFetch(`/assets/groups?customer_id=${order.customer_id}`);
  const [gid, setGid] = useState("");
  const link = async () => {
    try { const r = await api.post(`/orders/${order.id}/asset-group`, { asset_group_id: gid }); toast.success(`${order.order_no} linked to ${r.data.asset_group_code}`); onLinked(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="flex flex-wrap items-center gap-2 rounded border border-amber-200 bg-amber-50 p-2 text-xs" data-testid="dispatch-asset-link-group">
      <span className="text-amber-800">Link an Asset Group to {order.order_no} first:</span>
      <select className={`${sel} max-w-xs`} value={gid} onChange={(e) => setGid(e.target.value)} data-testid="dispatch-asset-group-select">
        <option value="">— pick —</option>{(data || []).filter((g) => g.status === "active").map((g) => <option key={g.id} value={g.id}>{g.asset_group_code} · {g.site_name || g.location_name || ""}</option>)}
      </select>
      <Button size="sm" disabled={!gid} onClick={link} data-testid="dispatch-asset-group-link">Link</Button>
    </div>
  );
}

function LineRow({ ln, v, set, opts }) {
  return (
    <div className="border-b border-slate-100 py-2" data-testid={`dispatch-asset-line-${ln.line_no}`}>
      <div className="flex flex-wrap items-start gap-3">
        <div className="flex-1 min-w-[200px] text-xs"><b>{ln.line_no}.</b> {ln.description}
          <div className="font-mono text-slate-500">{ln.item_code || "no item code"} · qty {ln.qty} {ln.uom}{ln.already ? ` · ${ln.already} already added` : ""}</div>
          {ln.serial_tracked && <span className="text-[11px] font-semibold text-amber-700" data-testid={`dispatch-asset-tracked-${ln.line_no}`}>Serial-tracked: serial numbers optional (one per unit)</span>}</div>
        <div className="w-20"><div className="text-[10px] uppercase text-slate-500">Units</div><Input type="number" min="0" className="h-8" value={v.units} onChange={(e) => set("units", e.target.value)} data-testid={`dispatch-asset-units-${ln.line_no}`} /></div>
        <div className="w-36"><div className="text-[10px] uppercase text-slate-500">Asset type</div><select className={sel} value={v.asset_type} onChange={(e) => set("asset_type", e.target.value)} data-testid={`dispatch-asset-type-${ln.line_no}`}><option value="">—</option>{opts("asset_type").map((t) => <option key={t}>{t}</option>)}</select></div>
      </div>
      <div className="mt-1"><SerialCapture qty={v.units} tracked={ln.serial_tracked} rows={v.rows} onChange={(rows) => set("rows", rows)} testid={`dispatch-asset-serials-${ln.line_no}`} /></div>
    </div>
  );
}

// Create Asset Units in the LM's Asset Group from a dispatch (challan or invoice), as allowed by the LM Type setting
export function DispatchAssetDialog({ dispatchId, onClose, onDone }) {
  const { opts } = useLookups();
  const [d, setD] = useState(null);
  const [rows, setRows] = useState({});
  const [billing, setBilling] = useState(true);
  const [skip, setSkip] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    api.get(`/assets/from-dispatch/${dispatchId}`).then((r) => {
      setD(r.data);
      setRows((old) => (Object.keys(old).length ? old : Object.fromEntries(r.data.lines.map((l) => [l.line_item_id, { units: l.units, asset_type: "", rows: l.serials || [] }]))));
    }).catch((e) => { toast.error(errMsg(e)); onClose(); });
  }, [dispatchId, tick]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!d) return null;
  const set = (id) => (k, v) => setRows({ ...rows, [id]: { ...rows[id], [k]: v } });
  const go = async () => {
    setBusy(true);
    try {
      const lines = Object.entries(rows).map(([id, v]) => ({ line_item_id: id, units: Number(v.units) || 0, asset_type: v.asset_type, serial_rows: cleanSerials((v.rows || []).slice(0, Number(v.units) || 0)) }));
      const r = await api.post(`/assets/from-dispatch/${dispatchId}`, { lines, billing_prompt: billing });
      toast.success(`${r.data.units.length} new${r.data.linked?.length ? ` + ${r.data.linked.length} existing` : ""} asset unit(s) in ${r.data.asset_group_code}`); onDone?.(); onClose();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const noAssets = async () => {
    try { await api.post(`/assets/from-dispatch/${dispatchId}/skip`, { note: skip }); toast.success("Recorded: no assets from this dispatch"); onDone?.(); onClose(); } catch (e) { toast.error(errMsg(e)); }
  };
  const g = d.order.asset_group_code;
  const doc = d.dispatch.document_type === "delivery_challan" ? `DC ${d.dispatch.dc_no || "—"}` : `Invoice ${d.dispatch.invoice_no || "—"}`;
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-4xl w-[95vw] max-h-[90vh] overflow-y-auto" data-testid="dispatch-asset-dialog">
        <DialogHeader><DialogTitle>Create assets from {d.dispatch.dispatch_no}</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500 -mt-2">{d.order.order_no}{d.order.contract_type ? ` (${d.order.contract_type})` : ""} · {doc} · {fmtDate(d.dispatch.dispatch_date)}{d.dispatch.site_context ? ` · ${d.dispatch.site_context}` : ""} · Assets after dispatch: <b data-testid="dispatch-asset-rule">{d.dispatch.rule}</b>{d.dispatch.rule === "Required" ? " (delivery can't be confirmed until done or skipped with a reason)" : ""}. Units go into Asset Group {g ? <b className="font-mono">{g}</b> : <b className="text-amber-700">not linked yet</b>}.</p>
        {!g && <LinkGroup order={d.order} onLinked={() => setTick((t) => t + 1)} />}
        <div>{d.lines.map((ln) => <LineRow key={ln.line_item_id} ln={ln} v={rows[ln.line_item_id]} set={set(ln.line_item_id)} opts={opts} />)}</div>
        <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={billing} onChange={(e) => setBilling(e.target.checked)} data-testid="dispatch-asset-billing" />Flag the matching billing period for review</label>
        {skip !== null && <div className="flex gap-2"><Input className="h-9" placeholder="Why no assets? (e.g. spares / consumables only)" value={skip} onChange={(e) => setSkip(e.target.value)} data-testid="dispatch-asset-skip-note" /><Button variant="outline" disabled={skip.trim().length < 3} onClick={noAssets} data-testid="dispatch-asset-skip-save">Confirm</Button></div>}
        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={onClose} data-testid="dispatch-asset-later">Decide later</Button>
          {skip === null && <Button variant="outline" onClick={() => setSkip("")} data-testid="dispatch-asset-skip">No assets from this dispatch</Button>}
          <Button onClick={go} disabled={busy || !g} data-testid="dispatch-asset-save">{busy ? "Adding…" : "Create assets"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// After a movement is saved: point to each dispatch whose LM Type requires assets after dispatch
export function promptDispatchAssets(prompts, navigate) {
  (prompts || []).forEach((p) => toast(`${p.dispatch_no} (${p.order_no}): create the assets - required before delivery is confirmed`, {
    duration: 15000, action: { label: "Create assets", onClick: () => navigate(p.prefix === "LMO" ? `/orders/${p.order_id}?tab=assets&dispatch=${p.dispatch_id}` : `/orders/${p.order_id}?tab=dispatches&assets=${p.dispatch_id}`) },
  }));
}
